import { CheckoutState } from './index';
import { ActionTree } from 'vuex';
import { IStore } from "@store/index";
import { AxiosError, AxiosResponse } from "axios";
// @ts-ignore
import {
    fetchBookingSteps,
    getPaxConnectImportUri,
    postResponse,
    restoreRequest,
    fetchPaymentMethods,
    saveRequest,
    logError
} from "@store/services/fetchCheckoutData";
import { EventBus } from "@store/services/checkoutClient";
import { FARE_DEFAULT, FARE_SMART } from "@constants/fares";
import { BookingStatus, RestoreStatus } from "@store/checkout/inquiry";
import { HTTP_BAD_REQUEST, HTTP_UNPROCESSABLE_ENTITY } from "@constants/api";
import { STEP_ACCOMMODATION, STEP_CAR_RENTAL, STEP_FLIGHTS, STEP_OVERVIEW, STEP_SERVICES } from "@constants/steps";
import PreInquiry from "@store/checkout/interfaces/preInquiry";

export default {
    nextNavigationObject({ commit, state, dispatch }, enablePriceSummary: boolean = true) {
        // @todo: NSDJW-6038: should be discussed with Sarah! (enablePriceSummary is false only on switch to payment step)
        if (enablePriceSummary) {
            dispatch('getPriceSummary');
        }
        commit('setIsValid', false);
        commit('incrementActiveIndex');
        commit('setActiveNavigation', state.navigation[state.activeIndex]);
    },
    async previousNavigationObject({ commit, state, dispatch }) {
        if (state.activeNavigation === STEP_OVERVIEW) {
            dispatch('clearVoucher');
            await dispatch('getPriceSummary');
        }

        commit('resetLoadingText');
        commit('decrementActiveIndex');
        commit('setActiveNavigation', state.navigation[state.activeIndex]);
    },
    setActive({ commit, state }, { navigationObject, index }) {
        if (index < state.activeIndex) {
            commit('resetLoadingText');
            commit('setActiveNavigation', navigationObject);
            commit('setActiveIndex', index);
        }
    },
    initCheckout({ commit, state }, { apiEndpoint, apiToken, isWhitelabel, checkoutSettings, checkout, agentImage, whitelabelSettings, disableSubmitButton, siteSettings, adyenConfig }) {
        commit('setSiteSettings', JSON.parse(siteSettings));
        commit('setApiEndpoint', apiEndpoint);
        commit('setApiToken', apiToken);
        commit('setIsWhitelabel', isWhitelabel);
        commit('setCheckoutSettings', JSON.parse(checkoutSettings));
        commit('setInitialCheckoutSettings', JSON.parse(checkoutSettings));
        commit('initCheckoutSummary');
        commit('setCheckout', checkout);
        commit('setAgentImage', agentImage);
        commit('disableCheckoutSubmitBtn', !!disableSubmitButton);
        commit('dateAndPrice/isInCheckout', true, { root: true });
        commit('setDepartureAirport', state.checkoutSettings.departureAirport);
        commit('setSessionId', state.checkoutSettings.sessionId);
        commit('isOnlyNonBinding', state.checkoutSettings.bindingMode === 'nonBinding');
        commit('setAdyenConfig', JSON.parse(adyenConfig))
        commit('setInternalCheckoutApiEndpoint');

        if (whitelabelSettings !== '') {
            commit('setWhitelabelSettings', JSON.parse(whitelabelSettings))
        }
    },
    async initCheckoutSteps({ commit, dispatch, state }, fare: string = '') {
        if (state.checkoutSettings.trip.id !== 0 && state.checkoutSettings.appointmentHash !== '') {
            await fetchBookingSteps(
                state.apiEndpoint,
                'GetBookingStepVisibilities',
                state.checkoutSettings.trip.id,
                state.checkoutSettings.appointmentHash,
                state.apiToken,
                state.checkoutSettings.sessionId,
                fare,
                state.checkoutSettings.locale
            )
                .then((response: AxiosResponse) => {
                    if (response.data.hasOwnProperty('payment')) {
                        commit('hasPayment', response.data.payment);
                    }

                    commit('initCheckoutSteps', response.data);
                })
                .catch((e: AxiosError) => {
                    /**
                   * "GetBookingStepVisibilities" is the first request on checkout.
                   * If the sessionId is invalid or empty the user should be redirected to step appointment.
                   *
                   *  Only handle this error, when restoreId is empty!
                   *  Restore Response will contain a valid sessionId. If not, this will be catched in the following requests.
                   *
                   */
                    if (state.checkoutSettings.restoreId === '') {
                        dispatch('handleError', e.response?.status);
                    }

                    const responseStatus = e.response ? e.response.status : 'none';
                    const message = `Error in initCheckoutSteps | Error code: 1628762962 | HTTP status: ${responseStatus} | ${e.message}`;

                    throw Error(message);
                });
        }
    },
    handleError({ state, commit, dispatch }, status: number|undefined) {
        if (status !== undefined && (status === HTTP_UNPROCESSABLE_ENTITY || status === HTTP_BAD_REQUEST)) {
            dispatch('openReloadOverlay');
            commit('hasError', true)

            return;
        }

        dispatch('openErrorOverlay');
    },
    async getPriceSummary({ commit, dispatch, state }) {
        if (!state.checkoutSummary.appointmentId) {
            return;
        }

        await postResponse(state.apiEndpoint, 'GetPriceSummary', state.apiToken, state.checkoutSummary, state.checkoutSettings.locale)
            .then((response: AxiosResponse) => {
                commit('setPriceSummary', response.data);
                dispatch('updateDataLayer')
            })
            .catch((e: AxiosError) => {
                dispatch('handleError', e.response?.status);

                const responseStatus = e.response ? e.response.status : 'none';
                const message = `Error in getPriceSummary | Error code: 1642576835622 | HTTP status: ${responseStatus} | ${e.message}`;

                throw Error(message);
            });
    },
    async updatePriceSummary({ dispatch }) {
        setTimeout( async () => {
            dispatch('getPriceSummary');
        }, 500);
    },
    openOverlay ({ commit }, { selector, modal, content }) {
        EventBus.$emit('doneLoading');

        const htmlBody = document.querySelector('html');
        const preventScroll = 'preventScroll-all';

        if (content) {
            commit('setModalContent', { modal: modal, content: content });
        }

        commit('setVisibility', { modal: modal, isVisible: true });

        if (window.iFrameResizer) {
            const overlay: HTMLElement | null = document.querySelector(selector + ' .js-overlay-content');
            if (overlay) {
                overlay.style.cssText = 'top:' + window.iFrameResizer.calcStartOffset() + 'px; min-height: unset; max-height:calc(100% - ' + window.iFrameResizer.calcStartOffset() + 'px)';
            }
        }

        if (htmlBody) {
            htmlBody.classList.add(preventScroll);
        }
    },
    closeOverlay ({ commit }, { modal }) {
        const htmlBody = document.querySelector('html');
        const preventScroll = 'preventScroll-all';

        commit('setVisibility', { modal: modal, isVisible: false })
        if (htmlBody) {
            htmlBody.classList.remove(preventScroll);
        }
    },
    openErrorOverlay({ commit, dispatch, state }) {
        commit('closeModals');
        dispatch('openOverlay', { selector: '.js-error-overlay', modal: state.visibility.error } );
    },
    openPersonalDataOverlay({ commit, dispatch, state }) {
        commit('closeModals');
        dispatch('openOverlay', { selector: '.js-personal-data-overlay', modal: state.visibility.personalData } );
    },
    openReloadOverlay({ commit, dispatch, state }) {
        commit('closeModals');
        dispatch('openOverlay', { selector: '.js-reload-overlay', modal: state.visibility.reload } );
    },
    updateDataLayer({ getters }) {
        const datalayer: Array<any> = window.dataLayer;

        if (datalayer[1].hasOwnProperty('ecommerce')) {
            datalayer[1].ecommerce.add.products[0].price = getters.totalPrice.toString();
        }
    },
    async saveRequest({ state, commit, dispatch }, currentStep: string) {
        const restoreId = state.checkoutSettings.restoreId;
        await saveRequest(state.apiEndpoint, state.checkoutSummary, currentStep, state.apiToken, restoreId, state.checkoutSettings.locale)
            .then((response: AxiosResponse) => {
                commit('setRestoreId', response.data);
            })
            .catch((error: AxiosError) => {
                const responseStatus = error.response ? error.response.status : 'none';
                const message = `Error in saveRequest | Error code: 1640009953 | HTTP status: ${responseStatus} | ${error.message}`;

                dispatch('openErrorOverlay');

                throw Error(message);
            })
    },
    async restoreCheckout({ state, commit, dispatch }) {
        if (state.checkoutSettings.restoreId !== '') {
            await restoreRequest(state.apiEndpoint, state.checkoutSettings.restoreId, state.apiToken, state.checkoutSettings.locale)
                .then(async (response: AxiosResponse) => {
                    const preInquiryRes = response.data as PreInquiry;

                    if (preInquiryRes.status === BookingStatus.ERROR) {
                        dispatch('openErrorOverlay');

                        return;
                    }

                    commit('setCustomSelection', true);
                    commit('setPreInquiry', preInquiryRes);
                    commit('setSessionId', preInquiryRes.bookingRequest.sessionId);

                    dispatch('openRestoreInfoOverlay');

                    /** update and restore booking steps */
                    commit('restoreCheckoutSteps');

                    if (appointmentIsRestorable(preInquiryRes.status)) {
                        /** override checkoutSummary with restored bookingRequest */
                        commit('restorePreInquiry');

                        /** init and restore fares steps */
                        if (state.preInquiry.bookingRequest.fare !== FARE_DEFAULT) {
                            await dispatch('getAvailableFares');
                            dispatch('updateFare', preInquiryRes.bookingRequest.fare);
                        }

                        /** initialize & restore rooms to display the contact form for travelers*/
                        await dispatch('initRooms');

                        /** Requests need to be done, to get all information in the overview step. Requirement: rooms must not be null! */
                        if (state.preInquiry.bookingRequest.appointmentId !== null && state.preInquiry.bookingRequest.rooms !== null) {
                            if (state.navigation.indexOf(STEP_ACCOMMODATION) > -1) {
                                await dispatch('getAvailableAccUpgrades');
                            }
                            if (state.navigation.indexOf(STEP_CAR_RENTAL) > -1) {
                                await dispatch('getAvailableCarRentals');
                            }
                            if (state.navigation.indexOf(STEP_FLIGHTS) > -1) {
                                await dispatch('getAvailableFlights');
                            }
                            if (state.navigation.indexOf(STEP_SERVICES) > -1) {
                                await dispatch('getAvailableServices');
                            }
                        }

                        dispatch('getPriceSummary');
                    }
                })
                .catch((error: AxiosError) => {
                    const responseStatus = error.response ? error.response.status : 'none';
                    const message = `Error in restoreCheckout | Error code: 1640011895 | HTTP status: ${responseStatus} | ${error.message}`;

                    dispatch('openErrorOverlay');

                    throw Error(message);
                })
        }
    },
    openRestoreInfoOverlay({ state, dispatch }) {
        dispatch('openOverlay', { selector: '.js-restore-info-overlay', modal: state.visibility.restoreInfo } );
    },
    async sendPreInquiryToCustomer({ state, dispatch }, email: string) {
        const preInquiryInfo: Record<string, any> = {
            email: email,
            restoreId: state.checkoutSettings.restoreId,
        };

        await postResponse(state.internalApiEndpoint, 'sendPreInquiryMail', '', preInquiryInfo, state.checkoutSettings.locale)
            .catch(e => {
                const responseStatus = e.response ? e.response.status : 'none';
                const message = `Error in sendPreInquiryToCustomer | Error code: 1640092274 | HTTP status: ${responseStatus} | ${e.message}`;

                dispatch('openErrorOverlay');

                throw Error(message);
            })
    },
    updateFare ({ state, commit, dispatch, rootGetters }, fare: string|undefined) {
        if (fare === undefined) {
            return;
        }

        if(fare === FARE_SMART) {
            commit('setBindingMode', 'direct');
        } else {
            // reset to default for non smart fares
            commit('resetBindingMode');
        }

        commit('setCurrentFare', fare);
        commit('updateFareInCheckoutSummary', fare)

        commit('funnel/addFareToDataLayer', fare, { root: true });
        dispatch('funnel/addSelectedToCheckoutFunnel', { type: 'fare', picked: rootGetters['checkout/getSelectedFare'] }, { root: true });

        dispatch('initCheckoutSteps', fare);
    },
    async handlePaxConnect ({ state, commit, dispatch }, bookingStep: string) {
        await getPaxConnectImportUri(state.apiEndpoint, state.apiToken, state.checkoutSummary, bookingStep, state.checkoutSettings.locale)
            .then((response: AxiosResponse) => {
                commit('setPaxConnectUri', response.data);

                dispatch('openOverlay', { selector: '.js-paxconnect-overlay', modal: state.visibility.paxConnect });
            })
            .catch(e => {
                const responseStatus = e.response ? e.response.status : 'none';
                const message = `Error in handlePaxConnect | Error code: 1667467227 | HTTP status: ${responseStatus} | ${e.message}`;

                dispatch('openErrorOverlay');

                throw Error(message);
            })
    },
    handleBottomSheetActiveState({ commit }, activeState: boolean) {
        commit('setBottomSheetActiveState', activeState);
    },
    updateSessionId({ state, commit }, sessionId: string) {
        commit('updateCheckoutSummarySessionId', sessionId);
    },
    changeBindingMode({ state, commit }, bindingMode: string) {
        commit('setBindingMode', bindingMode);

        if (bindingMode === 'nonBinding') {
            commit('resetContactPersons');
            commit('removePaymentStep');
            commit('resetDriverState');
            commit('setUseContactInfo', null);
        }
    },
    async setPaymentOptions({ state, commit }) {
        await fetchPaymentMethods(state.siteSettings)
            .then((response: AxiosResponse) => {
                commit('setPaymentOptions', response.data.response);
            })
            .catch(e => {
                const responseStatus = e.response ? e.response.status : 'none';
                const message = `Error in setPaymentOptions | Error code: 5542576720275 | HTTP status: ${responseStatus} | ${e.message}`;

                throw Error(message);
            })
    },
    async logCheckoutError({ state, dispatch }, error: any) {
        await logError(state.internalApiEndpoint, 'logError', error)
            .catch(e => {
                const responseStatus = e.response ? e.response.status : 'none';
                const message = `Error in logCheckoutError | Error code: 1693223090 | HTTP status: ${responseStatus} | ${e.message}`;

                throw Error(message);
            })
    },
} as ActionTree<CheckoutState, IStore>

function appointmentIsRestorable(status: BookingStatus|RestoreStatus) {
    return status === BookingStatus.SUCCESS || status === BookingStatus.APPOINTMENTDATA_CHANGED || status === BookingStatus.PRICE_CHANGED
}
